import React, { useEffect, useState } from 'react';
import LavorisService from '../../../services/LavorisService';
import { useAlert } from '../../../hooks/useAlert';
import { getErrorMessage } from '../../../utils';
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DynamicFileUpload from '../DynamicFileUpload';

const ACCEPT_FILES = "image/jpeg,image/jpg,image/png,image/gif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

type DBprops = {
  id: string | null;
  updateUploadedFiles: (files: File[]) => void;
};

type FileItem = { id: string; name?: string; [key: string]: any };

const DownloadButton = ({ id, updateUploadedFiles }: DBprops) => {
  const LavorisInstance = LavorisService.getInstance();
  const { showAlert } = useAlert();
  const [isReady, setIsReady] = useState(false);
  const [fileArray, setFileArray] = useState<FileItem[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const getAppointment = async () => {
    if (!id) return;
    setIsReady(false);
    try {
      const appointment = await LavorisInstance.TurnServices.getOneAppointment(id);
      const files = Array.isArray(appointment?.files) ? appointment.files : [];
      setFileArray(files);
    } finally {
      setIsReady(true);
    }
  };

  useEffect(() => {
    getAppointment();
  }, [id]);

  const handleDeleteFile = async (file: FileItem) => {
    if (!id) return;
    setDeletingId(file?.id ?? id);
    try {
      if (file?.id) {
        try {
          await LavorisInstance.TurnServices.deleteTurnFileByFileId(id, file.id);
        } catch {
          if (fileArray.length === 1) {
            await LavorisInstance.TurnServices.deleteTurnFile(id);
          } else {
            throw new Error('No se pudo eliminar el archivo');
          }
        }
      } else {
        await LavorisInstance.TurnServices.deleteTurnFile(id);
      }
      await getAppointment();
    } catch (err) {
      showAlert(getErrorMessage({ title: 'Error al eliminar el archivo', err }));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = (fileId: string) => {
    LavorisInstance.FilesService.getAWSTurnFile(fileId).catch((err) =>
      showAlert(getErrorMessage({ title: 'Error al descargar', err }))
    );
  };

  if (!isReady) {
    return <p className="text-gray-500">Cargando adjuntos...</p>;
  }

  return (
    <div className="space-y-4">
      {fileArray.length > 0 && (
        <div className="space-y-2">
          <p className="font-medium text-gray-700">Archivos adjuntos:</p>
          <ul className="list-none space-y-2">
            {fileArray.map((file) => (
              <li
                key={file.id}
                className="flex items-center justify-between gap-2 py-2 px-3 bg-gray-50 rounded border border-gray-200"
              >
                <span className="truncate text-sm" title={file.name || file.id}>
                  {file.name || file.id || 'Archivo'}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleDownload(file.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-sm font-medium text-gray-800 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Descargar
                    <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const fileName = file.name || file.id || 'este archivo';
                      if (window.confirm(`¿Está seguro que desea eliminar "${fileName}"?`)) {
                        handleDeleteFile(file);
                      }
                    }}
                    disabled={deletingId === file.id}
                    className="px-2 py-1 text-sm font-medium text-white bg-red-500 rounded hover:bg-red-600 disabled:opacity-50"
                  >
                    {deletingId === file.id ? 'Eliminando...' : 'Eliminar'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <DynamicFileUpload
        label={fileArray.length > 0 ? 'Agregar otro archivo (opcional)' : 'Cargar archivo (opcional)'}
        updateFilesCb={updateUploadedFiles}
        maxFileSizeInBytes={10 * 1024 * 1024}
        accept={ACCEPT_FILES}
        multiple
        buttonText="Agregar otro archivo"
      />
    </div>
  );
};

export default DownloadButton;
